import { useState, useRef, useEffect } from 'react';
import { useForm, Controller, type SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../ui/Input';
import Button from '../ui/Button';
import CropTypeCombobox from '../shared/CropTypeCombobox';
import { cultivarSchema, type CultivarFormData, PROGRAM_TYPE_LABELS } from '../../types/cultivar';
import type { Cultivar, CultivarProtection, CultivarProgram } from '../../types/cultivar';
import organizationService, { type OrgSearchResult } from '../../services/organization';
import cultivarService from '../../services/cultivar';
import { Leaf, Shield, Info, AlertCircle, Building2, Search, Loader2, X, Plus, Trash2, Globe, ExternalLink, FileCheck, ClipboardList } from 'lucide-react';

export interface ProtectionRow {
  id?: number;
  jurisdiction: string;
  type: string;
  registration_number: string;
  grant_date: string;
  expiry_date: string;
  authority: string;
  source_url: string;
  notes: string;
}

const JURISDICTION_OPTIONS = [
  { value: 'AU', label: 'Australia' },
  { value: 'US', label: 'United States' },
  { value: 'EU', label: 'European Union' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'INT', label: 'International (UPOV)' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'CL', label: 'Chile' },
  { value: 'OTHER', label: 'Other' },
];

const TYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  AU: [{ value: 'pbr', label: 'Plant Breeder\'s Rights (PBR)' }],
  US: [{ value: 'plant_patent', label: 'Plant Patent' }],
  EU: [{ value: 'cpvo', label: 'Community Plant Variety (CPVO)' }],
  NZ: [{ value: 'pvr', label: 'Plant Variety Rights (PVR)' }],
  INT: [{ value: 'upov', label: 'UPOV Registration' }],
  DEFAULT: [
    { value: 'pbr', label: 'PBR' },
    { value: 'plant_patent', label: 'Plant Patent' },
    { value: 'cpvo', label: 'CPVO' },
    { value: 'pvr', label: 'PVR' },
    { value: 'upov', label: 'UPOV' },
    { value: 'other', label: 'Other' },
  ],
};

const AUTHORITY_DEFAULTS: Record<string, string> = {
  'AU-pbr': 'IP Australia',
  'US-plant_patent': 'USPTO',
  'EU-cpvo': 'CPVO',
  'NZ-pvr': 'NZ Plant Variety Rights Office',
  'INT-upov': 'UPOV',
};

function getTypeOptions(jurisdiction: string) {
  return TYPE_OPTIONS[jurisdiction] || TYPE_OPTIONS.DEFAULT;
}

function getDefaultType(jurisdiction: string): string {
  const options = getTypeOptions(jurisdiction);
  return options[0]?.value || 'other';
}

function getDefaultAuthority(jurisdiction: string, type: string): string {
  return AUTHORITY_DEFAULTS[`${jurisdiction}-${type}`] || '';
}

function emptyProtectionRow(): ProtectionRow {
  return {
    jurisdiction: 'AU',
    type: 'pbr',
    registration_number: '',
    grant_date: '',
    expiry_date: '',
    authority: 'IP Australia',
    source_url: '',
    notes: '',
  };
}

function protectionFromExisting(p: CultivarProtection): ProtectionRow {
  return {
    id: p.id,
    jurisdiction: p.jurisdiction,
    type: p.type,
    registration_number: p.registration_number || '',
    grant_date: p.grant_date?.slice(0, 10) || '',
    expiry_date: p.expiry_date?.slice(0, 10) || '',
    authority: p.authority || '',
    source_url: p.source_url || '',
    notes: p.notes || '',
  };
}

interface CultivarFormProps {
  initialData?: Cultivar;
  /** Starting values for a new cultivar, e.g. from a quick evaluation's tag. */
  prefill?: { name?: string; crop_type_id?: number };
  onSubmit: (data: CultivarFormData, protections: ProtectionRow[], programIds: number[]) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

type TabKey = 'basic' | 'protection' | 'programs' | 'pbr';

function CultivarForm({ initialData, prefill, onSubmit, onCancel, isSubmitting = false }: CultivarFormProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [breederQuery, setBreederQuery] = useState('');
  const [breederResults, setBreederResults] = useState<OrgSearchResult[]>([]);
  const [showBreederDropdown, setShowBreederDropdown] = useState(false);
  const [loadingBreeders, setLoadingBreeders] = useState(false);
  const [selectedBreederName, setSelectedBreederName] = useState(initialData?.breeder_org_name || '');
  const breederDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [protections, setProtections] = useState<ProtectionRow[]>(
    initialData?.protections?.length
      ? initialData.protections.map(protectionFromExisting)
      : []
  );

  const [availablePrograms, setAvailablePrograms] = useState<CultivarProgram[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState<string | null>(null);
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>(
    initialData?.programs?.map((p) => p.id) ?? []
  );

  useEffect(() => {
    let cancelled = false;
    setProgramsLoading(true);
    setProgramsError(null);
    cultivarService
      .listPrograms({ active_only: true, mine: true })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setAvailablePrograms(res.data);
        } else {
          setProgramsError('Failed to load programs');
        }
      })
      .catch(() => {
        if (!cancelled) setProgramsError('Failed to load programs');
      })
      .finally(() => {
        if (!cancelled) setProgramsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleProgram = (programId: number) => {
    setSelectedProgramIds((prev) =>
      prev.includes(programId) ? prev.filter((id) => id !== programId) : [...prev, programId]
    );
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CultivarFormData>({
    resolver: zodResolver(cultivarSchema),
    defaultValues: {
      name: initialData?.name || prefill?.name || '',
      trade_name: initialData?.trade_name || '',
      botanical_name: initialData?.botanical_name || '',
      species: initialData?.species || '',
      crop_type_id: initialData?.crop_type_id ?? prefill?.crop_type_id,
      cultivar_role: initialData?.cultivar_role || 'scion',
      protection_status: initialData?.protection_status,
      breeder_org_id: initialData?.breeder_org_id,
      breeder_name: initialData?.breeder_name || '',
      origin_country: initialData?.origin_country || '',
      year_bred: initialData?.year_bred,
      chill_requirement_portions: initialData?.chill_requirement_portions,
      description: initialData?.description || '',
      is_public: initialData?.is_public ?? false,
      is_pbr_candidate: initialData?.is_pbr_candidate ?? false,
      first_sale_date_au: initialData?.first_sale_date_au || '',
      first_sale_date_overseas: initialData?.first_sale_date_overseas || '',
      first_sale_country_overseas: initialData?.first_sale_country_overseas || '',
      us_patent_number: initialData?.us_patent_number || '',
      breeding_history: initialData?.breeding_history || '',
      distinctness_summary: initialData?.distinctness_summary || '',
      quarantine_entry_date: initialData?.quarantine_entry_date?.slice(0, 10) || '',
      quarantine_release_date: initialData?.quarantine_release_date?.slice(0, 10) || '',
      quarantine_released: initialData?.quarantine_released ?? false,
      quarantine_permit_number: initialData?.quarantine_permit_number || '',
      quarantine_notes: initialData?.quarantine_notes || '',
    },
  });

  const searchBreederOrganisations = async (query: string) => {
    if (query.length < 2) {
      setBreederResults([]);
      return;
    }

    setLoadingBreeders(true);
    try {
      const response = await organizationService.searchOrganizations({
        name: query,
        stakeholder_type: 'breeder',
      });
      if (response.success && response.data) {
        setBreederResults(response.data);
      }
    } catch (error) {
      console.error('Failed to search breeder organisations:', error);
    } finally {
      setLoadingBreeders(false);
    }
  };

  const watchProtectionStatus = watch('protection_status');
  const isOpenCultivar = watchProtectionStatus === 'open';
  const breederOrgId = watch('breeder_org_id');

  const tabs: { key: TabKey; label: string; icon: typeof Leaf }[] = [
    { key: 'basic', label: 'Basic Info', icon: Info },
    { key: 'protection', label: 'IP Protection', icon: Shield },
    { key: 'programs', label: 'Programs', icon: ClipboardList },
    { key: 'pbr', label: 'PBR Workflow', icon: FileCheck },
  ];
  const watchIsPbrCandidate = watch('is_pbr_candidate');
  const watchFirstSaleAu = watch('first_sale_date_au');
  const watchFirstSaleOverseas = watch('first_sale_date_overseas');
  const watchQuarantineRelease = watch('quarantine_release_date');

  const handleInvalidSubmit: SubmitErrorHandler<CultivarFormData> = (fieldErrors) => {
    if (fieldErrors.name || fieldErrors.cultivar_role || fieldErrors.year_bred || fieldErrors.chill_requirement_portions || fieldErrors.crop_type_id || fieldErrors.origin_country) {
      setActiveTab('basic');
      return;
    }
    if (fieldErrors.protection_status) {
      setActiveTab('protection');
      return;
    }
    setActiveTab('pbr');
  };

  const addProtectionRow = () => {
    setProtections(prev => [...prev, emptyProtectionRow()]);
  };

  const removeProtectionRow = (index: number) => {
    setProtections(prev => prev.filter((_, i) => i !== index));
  };

  const updateProtectionRow = (index: number, field: keyof ProtectionRow, value: string) => {
    setProtections(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-set type and authority when jurisdiction changes
      if (field === 'jurisdiction') {
        updated[index].type = getDefaultType(value);
        updated[index].authority = getDefaultAuthority(value, updated[index].type);
      }
      if (field === 'type') {
        updated[index].authority = getDefaultAuthority(updated[index].jurisdiction, value);
      }

      return updated;
    });
  };

  const handleFormSubmit = (data: CultivarFormData) => {
    onSubmit(data, isOpenCultivar ? [] : protections, selectedProgramIds);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)} className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-neutral-200">
        <nav className="flex flex-wrap gap-x-2 sm:gap-x-4 gap-y-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const hasError = tab.key === 'basic'
              ? !!errors.name
              : false;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.key === 'protection' && protections.length > 0 && !isOpenCultivar && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700">
                    {protections.length}
                  </span>
                )}
                {tab.key === 'programs' && selectedProgramIds.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700">
                    {selectedProgramIds.length}
                  </span>
                )}
                {hasError && (
                  <AlertCircle className="h-4 w-4 text-error-500" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Cultivar Name *"
                {...register('name')}
                error={errors.name?.message}
                placeholder="e.g., Fuji, Pink Lady, Gala"
              />
              <Input
                label="Trade Name"
                {...register('trade_name')}
                error={errors.trade_name?.message}
                placeholder="Commercial trade name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Botanical Name"
                {...register('botanical_name')}
                error={errors.botanical_name?.message}
                placeholder="e.g., Malus domestica"
              />
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-neutral-700">
                  Crop Type
                </label>
                <Controller
                  name="crop_type_id"
                  control={control}
                  render={({ field }) => (
                    <CropTypeCombobox
                      value={field.value as number | null | undefined}
                      onChange={(id) => field.onChange(id)}
                      error={errors.crop_type_id?.message}
                    />
                  )}
                />
                <p className="text-xs text-neutral-500">
                  The crop this cultivar belongs to — apple, pear, cherry. Use Botanical Name
                  above for the scientific species.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-neutral-700">
                Cultivar Role *
              </label>
              <select
                {...register('cultivar_role')}
                className={`input-professional w-full ${errors.cultivar_role ? 'border-error-300' : ''}`}
              >
                <option value="scion">Scion</option>
                <option value="rootstock">Rootstock</option>
                {/* Only offered when the record already carries it, so an edit
                    preserves it rather than silently demoting the cultivar,
                    without inviting it as a choice on new entries. */}
                {initialData?.cultivar_role === 'dual' && (
                  <option value="dual">Dual (scion and rootstock)</option>
                )}
              </select>
              {errors.cultivar_role && (
                <p className="text-sm text-error-600">{errors.cultivar_role.message}</p>
              )}
              <p className="text-xs text-neutral-500">
                Choose whether this entry represents a scion cultivar or a rootstock selection.
                {initialData?.cultivar_role === 'dual' &&
                  ' This cultivar is recorded as serving both roles; leave it set to Dual to keep it available in rootstock pickers.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label="Origin Country"
                {...register('origin_country')}
                error={errors.origin_country?.message}
                placeholder="e.g., USA, Australia"
              />
              <Input
                label="Year Bred"
                type="number"
                {...register('year_bred', { setValueAs: (v) => v === '' || v === null ? null : parseInt(v, 10) })}
                error={errors.year_bred?.message}
                placeholder="e.g., 2010"
                min={1800}
                max={new Date().getFullYear()}
              />
              <Input
                label="Chill requirement (chill portions)"
                type="number"
                {...register('chill_requirement_portions', { setValueAs: (v) => v === '' || v === null ? null : parseInt(v, 10) })}
                error={errors.chill_requirement_portions?.message}
                placeholder="e.g., 60"
                min={0}
                max={200}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-neutral-700">
                Description
              </label>
              <textarea
                {...register('description')}
                className="input-professional w-full min-h-[120px]"
                placeholder="Describe the cultivar, its origins, notable qualities, and uses..."
              />
            </div>

            {/* Each organisation keeps its own register (2026-10-20). There is
                no "every organisation" choice any more: another organisation sees
                a cultivar through a licence, an evaluator grant or a licensor tick. */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <p className="text-sm font-semibold text-neutral-700">Who can see this cultivar</p>
              <p className="mt-1 text-xs text-neutral-600">
                Your organisation, and the organisations you license it to, name as a licensor or tick for
                sublicensing. To let another organisation look at it and trial it, give it an evaluation licence
                or add the organisation on the Evaluators tab. Other organisations cannot find it.
              </p>
            </div>

            {/* Post-entry quarantine */}
            <fieldset className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <legend className="px-1 text-sm font-semibold text-neutral-900">Quarantine</legend>
              <p className="text-xs text-neutral-500">
                For imported material held in post-entry quarantine before it can be propagated. Leave blank if it
                never was.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Entered quarantine"
                  type="date"
                  {...register('quarantine_entry_date')}
                  error={errors.quarantine_entry_date?.message}
                />
                <Input
                  label="Released from quarantine"
                  type="date"
                  {...register('quarantine_release_date')}
                  error={errors.quarantine_release_date?.message}
                />
              </div>
              {!watchQuarantineRelease && (
                <label className="flex items-start gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    {...register('quarantine_released')}
                    className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0">Released, date not known</span>
                </label>
              )}
              <Input
                label="Import permit number"
                {...register('quarantine_permit_number')}
                error={errors.quarantine_permit_number?.message}
              />
              <div>
                <label htmlFor="quarantine_notes" className="block text-sm font-medium text-neutral-700 mb-1">
                  Quarantine notes
                </label>
                <textarea
                  id="quarantine_notes"
                  rows={2}
                  {...register('quarantine_notes')}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Facility, conditions, testing…"
                />
              </div>
            </fieldset>
          </div>
        )}

        {/* IP Protection Tab */}
        {activeTab === 'protection' && (
          <div className="space-y-6">
            {/* Open Cultivar Toggle */}
            <div className="bg-neutral-50 rounded-lg p-4 flex items-start gap-3">
              <input
                type="checkbox"
                id="is_open_cultivar"
                checked={isOpenCultivar}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('protection_status', 'open');
                  } else {
                    // Undefined is left out of the PUT, and the server keeps
                    // 'open'. Not open with no protection yet (still in test)
                    // is 'pending', as the server derives it; saved protections
                    // re-derive it.
                    const previous = initialData?.protection_status;
                    setValue('protection_status', previous && previous !== 'open' ? previous : 'pending');
                  }
                }}
                className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <label htmlFor="is_open_cultivar" className="text-sm font-semibold text-neutral-700 cursor-pointer">
                  Open Cultivar (no IP protection)
                </label>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Check this only for a variety anyone may grow and propagate. A cultivar still in test, before
                  PBR or a patent is applied for, is not open: leave this unticked. Open or not, only your
                  organisation and the organisations you license it to can see it.
                </p>
              </div>
            </div>

            {isOpenCultivar && (
              <div className="rounded-lg p-4 bg-success-50 border border-success-200">
                <div className="flex items-start gap-3">
                  <Leaf className="h-5 w-5 mt-0.5 text-success-600" />
                  <div>
                    <p className="font-medium text-success-800">Open Cultivar</p>
                    <p className="text-sm mt-1 text-success-700">
                      This cultivar will be registered without IP protection. No protection registrations are required.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Breeder Section */}
            {!isOpenCultivar && (
              <div className="bg-neutral-50 rounded-lg p-6">
                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-neutral-600" />
                  Breeder Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-neutral-700">
                      Breeder Organisation (In System)
                    </label>
                    {breederOrgId && selectedBreederName ? (
                      <div className="flex items-center justify-between px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-primary-600" />
                          <span className="text-sm font-medium text-primary-900">{selectedBreederName}</span>
                        </div>
                        <button
                          type="button"
                          className="text-primary-500 hover:text-primary-700"
                          onClick={() => {
                            setValue('breeder_org_id', undefined);
                            setSelectedBreederName('');
                            setBreederQuery('');
                          }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <input
                            type="text"
                            value={breederQuery}
                            onChange={(e) => {
                              const query = e.target.value;
                              setBreederQuery(query);
                              setShowBreederDropdown(true);
                              if (breederDebounceRef.current) {
                                clearTimeout(breederDebounceRef.current);
                              }
                              breederDebounceRef.current = setTimeout(() => {
                                searchBreederOrganisations(query);
                              }, 300);
                            }}
                            onFocus={() => setShowBreederDropdown(true)}
                            onBlur={() => setTimeout(() => setShowBreederDropdown(false), 200)}
                            className="input-professional w-full pl-10"
                            placeholder="Search breeder organisation..."
                          />
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                            {loadingBreeders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                          </div>
                        </div>

                        {showBreederDropdown && breederResults.length > 0 && (
                          <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                            {breederResults.map((org) => (
                              <button
                                key={org.id}
                                type="button"
                                className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setValue('breeder_org_id', org.id);
                                  setValue('breeder_name', '');
                                  setSelectedBreederName(org.name);
                                  setBreederQuery('');
                                  setShowBreederDropdown(false);
                                }}
                              >
                                <p className="text-sm font-medium text-neutral-900">{org.name}</p>
                                {org.stakeholder_types?.length > 0 && (
                                  <p className="text-xs text-neutral-500">{org.stakeholder_types.join(', ')}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      type="hidden"
                      {...register('breeder_org_id', {
                        setValueAs: (value) => (value === '' || value == null ? undefined : Number(value)),
                      })}
                    />
                    <p className="text-xs text-neutral-500">Select this when the breeder already exists as an organisation.</p>
                  </div>

                  <Controller
                    name="breeder_name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="External Breeder Name"
                        value={(field.value as string) || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          if (value.trim()) {
                            setValue('breeder_org_id', undefined);
                            setSelectedBreederName('');
                          }
                        }}
                        placeholder="Breeder not in system"
                      />
                    )}
                  />
                </div>
              </div>
            )}

            {/* Protections Section */}
            {!isOpenCultivar && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary-600" />
                    Protection Registrations
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addProtectionRow}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Protection
                  </Button>
                </div>

                {protections.length === 0 && (
                  <div className="text-center py-8 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200">
                    <Shield className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
                    <p className="text-sm text-neutral-500">No protection registrations added yet.</p>
                    <p className="text-xs text-neutral-400 mt-1">Click "Add Protection" to record PBR, patent, or other IP registrations.</p>
                  </div>
                )}

                {protections.map((protection, index) => (
                  <div key={index} className="bg-neutral-50 rounded-lg p-5 border border-neutral-200">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-neutral-700">
                        {JURISDICTION_OPTIONS.find(j => j.value === protection.jurisdiction)?.label || protection.jurisdiction}
                        {' — '}
                        {getTypeOptions(protection.jurisdiction).find(t => t.value === protection.type)?.label || protection.type}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeProtectionRow(index)}
                        className="text-neutral-400 hover:text-error-600 transition-colors"
                        title="Remove protection"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Jurisdiction</label>
                        <select
                          value={protection.jurisdiction}
                          onChange={(e) => updateProtectionRow(index, 'jurisdiction', e.target.value)}
                          className="input-professional w-full text-sm"
                        >
                          {JURISDICTION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Type</label>
                        <select
                          value={protection.type}
                          onChange={(e) => updateProtectionRow(index, 'type', e.target.value)}
                          className="input-professional w-full text-sm"
                        >
                          {getTypeOptions(protection.jurisdiction).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Registration Number</label>
                        <input
                          type="text"
                          value={protection.registration_number}
                          onChange={(e) => updateProtectionRow(index, 'registration_number', e.target.value)}
                          className="input-professional w-full text-sm"
                          placeholder="e.g., PBR 1234"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Grant Date</label>
                        <input
                          type="date"
                          value={protection.grant_date}
                          onChange={(e) => updateProtectionRow(index, 'grant_date', e.target.value)}
                          className="input-professional w-full text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Expiry Date</label>
                        <input
                          type="date"
                          value={protection.expiry_date}
                          onChange={(e) => updateProtectionRow(index, 'expiry_date', e.target.value)}
                          className="input-professional w-full text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Authority</label>
                        <input
                          type="text"
                          value={protection.authority}
                          onChange={(e) => updateProtectionRow(index, 'authority', e.target.value)}
                          className="input-professional w-full text-sm"
                          placeholder="e.g., IP Australia"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600 flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          Source URL
                        </label>
                        <input
                          type="text"
                          value={protection.source_url}
                          onChange={(e) => updateProtectionRow(index, 'source_url', e.target.value)}
                          className="input-professional w-full text-sm"
                          placeholder="https://..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-neutral-600">Notes</label>
                        <input
                          type="text"
                          value={protection.notes}
                          onChange={(e) => updateProtectionRow(index, 'notes', e.target.value)}
                          className="input-professional w-full text-sm"
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Protection Status Notice */}
            {!isOpenCultivar && protections.length > 0 && (
              <div className="rounded-lg p-4 bg-success-50 border border-success-200">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 mt-0.5 text-success-600" />
                  <div>
                    <p className="font-medium text-success-800">
                      {protections.length} Protection Registration{protections.length !== 1 ? 's' : ''} Configured
                    </p>
                    <p className="text-sm mt-1 text-success-700">
                      Protection status will be automatically derived from registration dates.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div className="space-y-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 mt-0.5 text-primary-600" />
              <div className="text-sm text-primary-800">
                Assign this cultivar to one or more royalty / licensing programs. Program
                membership drives reporting cadence, applicable terms, and royalty rates
                downstream (orders, declarations, sublicences). A cultivar can belong to
                multiple programs.
              </div>
            </div>

            {programsLoading && (
              <div className="flex items-center justify-center py-8 text-neutral-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading programs...
              </div>
            )}

            {programsError && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4 text-sm text-error-700">
                {programsError}
              </div>
            )}

            {!programsLoading && !programsError && availablePrograms.length === 0 && (
              <div className="text-center py-8 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200">
                <ClipboardList className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
                <p className="text-sm text-neutral-500">No active programs available.</p>
                <p className="text-xs text-neutral-400 mt-1">
                  Create programs from the Cultivar Admin area first.
                </p>
              </div>
            )}

            {!programsLoading && !programsError && availablePrograms.length > 0 && (
              <div className="space-y-2">
                {availablePrograms.map((prog) => {
                  const checked = selectedProgramIds.includes(prog.id);
                  const typeLabel =
                    prog.program_type === 'other' && prog.custom_type_label
                      ? prog.custom_type_label
                      : PROGRAM_TYPE_LABELS[prog.program_type];
                  return (
                    <label
                      key={prog.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-neutral-200 hover:bg-neutral-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleProgram(prog.id)}
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-neutral-900">{prog.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700">
                            {typeLabel}
                          </span>
                          {prog.organisation_name && (
                            <span className="text-xs text-neutral-500">
                              {prog.organisation_name}
                            </span>
                          )}
                        </div>
                        {prog.description && (
                          <p className="text-sm text-neutral-600 mt-1">{prog.description}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PBR Workflow Tab */}
        {activeTab === 'pbr' && (
          <div className="space-y-6">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-sm text-primary-800">
                Flag this cultivar as a PBR candidate to start tracking a Plant Breeder's Rights
                application. First-sale dates drive the Part 1 filing deadline: 1 year from the
                AU first sale or 6 years from the overseas first sale, whichever is earlier.
              </p>
            </div>

            <label className="flex items-start gap-3 p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 cursor-pointer">
              <input
                type="checkbox"
                {...register('is_pbr_candidate')}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <div className="font-medium text-neutral-900">Candidate for PBR</div>
                <div className="text-sm text-neutral-600">
                  Show this cultivar on the PBR dashboard and candidates list.
                </div>
              </div>
            </label>

            {watchIsPbrCandidate && !watchFirstSaleAu && !watchFirstSaleOverseas && (
              <div className="rounded-lg p-4 bg-primary-50 border border-primary-200 flex items-start gap-3">
                <Info className="h-5 w-5 mt-0.5 text-primary-600" />
                <p className="text-sm text-primary-800">
                  First-sale dates are optional — leave blank if this variety hasn't been sold yet.
                  Once a date is recorded, the Part 1 filing deadline is calculated automatically
                  (1 year from AU first sale, or 6 years from overseas first sale, whichever is earlier).
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="date"
                label="First sale — Australia"
                {...register('first_sale_date_au')}
                error={errors.first_sale_date_au?.message}
              />
              <Input
                type="date"
                label="First sale — Overseas"
                {...register('first_sale_date_overseas')}
                error={errors.first_sale_date_overseas?.message}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="First-sale country (overseas)"
                {...register('first_sale_country_overseas')}
                error={errors.first_sale_country_overseas?.message}
                placeholder="e.g., United States"
              />
              <Input
                label="US Patent Number"
                {...register('us_patent_number')}
                error={errors.us_patent_number?.message}
                placeholder="e.g., PP34567"
              />
            </div>
            <p className="text-xs text-neutral-500 -mt-2">
              US patents are often a source for Part 1 data (parentage, breeding history,
              distinctness). Enter the plant patent number if one exists.
            </p>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-neutral-700">Breeding history</label>
              <textarea
                {...register('breeding_history')}
                rows={4}
                className="input-professional w-full"
                placeholder="Describe parentage, breeding method, and history. Usually sourceable from the US patent or UPOV description."
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-neutral-700">Distinctness summary</label>
              <textarea
                {...register('distinctness_summary')}
                rows={4}
                className="input-professional w-full"
                placeholder="Summarise how this variety is distinct from similar varieties of common knowledge."
              />
            </div>

            {watchIsPbrCandidate && initialData?.id && (
              <div className="rounded-lg p-4 bg-neutral-50 border border-neutral-200">
                <p className="text-sm text-neutral-700">
                  Manage PBR applications for this cultivar →{' '}
                  <a
                    href={`/cultivar/pbr/applications?cultivar_id=${initialData.id}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    Open PBR workspace
                  </a>
                </p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-neutral-200">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <div className="flex items-center gap-3">
          {/* Tab Navigation Helpers */}
          {activeTab !== 'basic' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const currentIndex = tabs.findIndex(t => t.key === activeTab);
                if (currentIndex > 0) {
                  setActiveTab(tabs[currentIndex - 1].key);
                }
              }}
            >
              Previous
            </Button>
          )}

          {activeTab !== tabs[tabs.length - 1].key && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const currentIndex = tabs.findIndex(t => t.key === activeTab);
                if (currentIndex < tabs.length - 1) {
                  setActiveTab(tabs[currentIndex + 1].key);
                }
              }}
            >
              Next
            </Button>
          )}

          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            {initialData ? 'Update Cultivar' : 'Register Cultivar'}
          </Button>
        </div>
      </div>

    </form>
  );
}

export default CultivarForm;
